import {
  Bell, Layers, Building2, Circle, Filter, Volume2, VolumeX, Droplet, Plus,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useGameStore } from '@/state/gameStore';
import { useViewport } from '@/hooks/useViewport';
import { isActiveMissionStatus } from '@/data/missionGenerator';
import type { LayerToggles } from '@/state/types';

/**
 * Phase 13.10 — `showAcquirable` toggles the visibility of NPC station markers
 *   (the semi-transparent acquire-targets). Default off.
 *
 * Phase 13.11 — The Siren-/Bell-icon is no longer a layer toggle. It opens the
 *   MainPanel directly on the "missions" tab, with a counter badge that shows
 *   the number of active (open + dispatched + onscene) missions. Mission
 *   markers themselves are always visible.
 */

type LayerToolKey = Exclude<keyof LayerToggles, 'missions'>;
type ToolKind = 'layer' | 'mute' | 'filter' | 'bell' | 'showAcquirable';
type ToolKey = LayerToolKey | 'filter' | 'mute' | 'bell' | 'showAcquirable';

interface ToolItem {
  key: ToolKey;
  icon: LucideIcon;
  label: string;
  kind: ToolKind;
}

const TOOLS: ToolItem[] = [
  { key: 'bell', icon: Bell, label: 'Einsätze öffnen', kind: 'bell' },
  { key: 'buildings3d', icon: Layers, label: '3D-Gebäude', kind: 'layer' },
  { key: 'stations', icon: Building2, label: 'Wachen-Marker', kind: 'layer' },
  { key: 'showAcquirable', icon: Building2, label: 'Kaufbare Wachen anzeigen', kind: 'showAcquirable' },
  { key: 'radius', icon: Circle, label: 'Einsatzgebiet', kind: 'layer' },
  { key: 'hydrants', icon: Droplet, label: 'Hydranten', kind: 'layer' },
  { key: 'mute', icon: Volume2, label: 'Sound', kind: 'mute' },
  { key: 'filter', icon: Filter, label: 'Filter (Phase 6)', kind: 'filter' },
];

/**
 * Floating toolbar.
 *   Desktop: vertical, right edge centered.
 *   Tablet: vertical, right edge centered (44 px buttons).
 *   Mobile: horizontal bar pinned bottom-center, larger 48 px buttons.
 */
export function MapToolbar() {
  const layers = useGameStore((s) => s.uiPrefs.layerToggles);
  const muted = useGameStore((s) => s.uiPrefs.muted);
  const showAcquirable = useGameStore((s) => s.uiPrefs.showAcquirableStations);
  const toggleLayer = useGameStore((s) => s.toggleLayer);
  const toggleMute = useGameStore((s) => s.toggleMute);
  const toggleShowAcquirable = useGameStore((s) => s.toggleShowAcquirableStations);
  const openMainPanel = useGameStore((s) => s.openMainPanel);
  const closeMainPanel = useGameStore((s) => s.closeMainPanel);
  const mainPanelOpen = useGameStore((s) => s.mainPanelOpen);
  const mainPanelTab = useGameStore((s) => s.mainPanelTab);
  const missions = useGameStore((s) => s.missions);
  const pushToast = useGameStore((s) => s.pushToast);
  const viewport = useViewport();

  const isMobile = viewport === 'mobile';

  // Phase 13.11 — Counter badge for the bell.
  const activeMissionCount = missions.filter((m) => isActiveMissionStatus(m.status)).length;

  return (
    <div
      className={[
        'pointer-events-auto fixed z-20 flex gap-1.5 rounded-2xl border border-white/5 p-1.5 shadow-xl backdrop-blur-md',
        isMobile
          ? 'bottom-3 left-1/2 -translate-x-1/2'
          : 'right-4 top-1/2 -translate-y-1/2 flex-col',
      ].join(' ')}
      style={{ background: 'rgba(20, 24, 36, 0.9)' }}
      role="toolbar"
      aria-label="Karten-Werkzeuge"
    >
      {TOOLS.map((t) => {
        let active = false;
        let Icon: LucideIcon = t.icon;
        let onClick: () => void;
        let title = t.label;
        let badge: number | null = null;
        let overlayPlus = false;

        if (t.kind === 'layer') {
          active = layers[t.key as LayerToolKey];
          onClick = () => toggleLayer(t.key as LayerToolKey);
        } else if (t.kind === 'mute') {
          active = !muted;
          Icon = muted ? VolumeX : Volume2;
          title = muted ? 'Sound aus — klicken zum Aktivieren' : 'Sound an — klicken zum Stummschalten';
          onClick = () => toggleMute();
        } else if (t.kind === 'bell') {
          // Phase 16.6 — Toggle: bei offenem Missions-Tab schließt der Klick;
          // bei anderem Tab wechselt er zu Missions; sonst öffnet er.
          const panelOpenOnMissions = mainPanelOpen && mainPanelTab === 'missions';
          active = panelOpenOnMissions;
          badge = activeMissionCount;
          onClick = () => {
            if (panelOpenOnMissions) {
              closeMainPanel();
            } else {
              openMainPanel('missions');
            }
          };
          title = panelOpenOnMissions
            ? 'Einsätze ausblenden'
            : activeMissionCount > 0
              ? `${activeMissionCount} aktive Einsätze — öffnen`
              : 'Einsätze öffnen';
        } else if (t.kind === 'showAcquirable') {
          // Phase 13.10 — Toggle for NPC station markers.
          active = !!showAcquirable;
          overlayPlus = true;
          onClick = () => toggleShowAcquirable();
        } else {
          onClick = () =>
            pushToast({ kind: 'info', text: 'Filter-Funktion kommt in der nächsten Phase.' });
        }

        const sizeClass = isMobile ? 'h-12 w-12' : 'h-11 w-11';

        return (
          <button
            key={t.key}
            type="button"
            onClick={onClick}
            aria-label={title}
            aria-pressed={
              t.kind === 'layer' || t.kind === 'mute' || t.kind === 'showAcquirable'
                ? active
                : undefined
            }
            title={title}
            className={[
              'relative flex items-center justify-center rounded-xl transition',
              sizeClass,
              (t.kind === 'layer' ||
                t.kind === 'mute' ||
                t.kind === 'showAcquirable' ||
                t.kind === 'bell') &&
              active
                ? 'bg-accent-dispatch text-white shadow'
                : 'bg-bg-elevated/40 text-fg-muted hover:bg-bg-elevated hover:text-fg-primary',
            ].join(' ')}
          >
            <Icon className="h-4 w-4" aria-hidden />
            {overlayPlus && (
              <span
                aria-hidden
                className="pointer-events-none absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-status-available text-[9px] font-bold text-bg-primary shadow"
              >
                <Plus className="h-2.5 w-2.5" aria-hidden />
              </span>
            )}
            {badge !== null && badge > 0 && (
              <span
                aria-hidden
                className="pointer-events-none absolute -top-1 -right-1 flex min-h-4 min-w-4 items-center justify-center rounded-full bg-accent-emergency px-1 text-[10px] font-bold text-white shadow"
              >
                {badge > 99 ? '99+' : badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
